import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import { rightDirectionIcon, leftDirectionIcon } from '../../../assets';
import { getFacilities } from '../../../utils/getToken';
import { switchToProvider } from '../../../utils/roleSwitch';
import FacilityService from '../../../services/FacilityService';

interface FacilityDropdownProps {
  onFacilitySelect?: (facility: any) => void;
  variant?: 'desktop' | 'mobile';
}

const FacilityDropdown: React.FC<FacilityDropdownProps> = ({
  onFacilitySelect,
  variant = 'desktop'
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // Seed from the login (localStorage) list for an instant render, then refresh
  // from the API on open so newly-added facilities appear without a re-login.
  const [facilities, setFacilities] = useState<any[]>(getFacilities() || []);

  const loadFacilities = async () => {
    try {
      const response = await FacilityService.getAllFacilities();
      if (response?.success && response?.facilities?.length > 0) {
        setFacilities(response.facilities);
      }
    } catch (error) {
      console.error('Failed to load facilities', error);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    loadFacilities();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFacilitySelect = (facility: any) => {
    if (onFacilitySelect) {
      onFacilitySelect(facility);
    }

    // Navigate to provider with refresh token and facility ID
    const success = switchToProvider(facility.id);
    if (!success) {
      console.error('Failed to switch to provider');
    }
    handleClose();
  };

  const buttonSx = variant === 'desktop' 
    ? {
        paddingTop: "6px",
        paddingBottom: "6px",
        minWidth: "180px",
        height: "38px",
        border: "1px solid #005596",
        color: "#005596",
        backgroundColor: "#FFFFFF",
        textTransform: "none" as const,
        fontSize: "14px",
        fontWeight: 500,
      }
    : {
        paddingTop: "8px",
        paddingBottom: "8px",
        height: "40px",
        border: "1px solid #005596",
        color: "#005596",
        backgroundColor: "#FFFFFF",
        textTransform: "none" as const,
        fontSize: "14px",
        fontWeight: 500,
        width: "100%",
      };

  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={
          <div className="flex flex-col cursor-pointer items-center mr-2 -space-y-0.5 space-x-2">
            <img src={rightDirectionIcon} alt="switch" className="w-3 h-3" />
            <img src={leftDirectionIcon} alt="switch" className="w-3 h-3" />
          </div>
        }
        onClick={handleClick}
        sx={buttonSx}
      >
        Switch to Provider
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: variant === 'desktop' ? '180px' : '280px',
            maxHeight: '300px',
            overflow: 'auto',
          }
        }}
      >
        {facilities && facilities.length > 0 ? (
          facilities.map((facility) => (
            <MenuItem
              key={facility.id}
              onClick={() => handleFacilitySelect(facility)}
              sx={{
                padding: '8px 16px',
                fontSize: '14px',
              }}
            >
              <ListItemText primary={facility.name} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemText primary="No facilities available" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default FacilityDropdown;

